import React from 'react';
import type { FormikProps } from '@axiom/ui';
import {
  Card,
  CardHeader,
  CardSection,
  DateInput,
  Dropdown,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  LayoutItem,
  Location,
  SmallHeader,
  Well,
} from '@axiom/ui';
import type { EstimatedDuration } from '@axiom/const';
import {
  billingUnitValues,
  estimatedDurationValues,
  PositionsConst,
  ProjectNeedsDefaultRemoteLocation,
  rawDateInputDirections,
  selfServiceCountryCodes,
  worksiteValues,
} from '@axiom/const';
import type { Account, ProjectNeedsWithLegalSkills } from '@axiom/validation';

import { EngagementUtil } from '../../utils/engagement-util';

import { DirectEngagementsFormIntakeUtil } from './direct-engagements-form-intake-util';

type ScheduleValues = Partial<ProjectNeedsWithLegalSkills>;

export type DirectEngagementsFormScheduleCardProps = {
  account: Pick<Account, 'hqAddressCountry'>;
  companyLocationInputValue: ScheduleValues['companyLocation'];
  setCompanyLocationInputValue: (
    value: ScheduleValues['companyLocation']
  ) => void;
  setShowWorksiteModal: (show: boolean) => void;
  setValues: FormikProps<ScheduleValues>['setValues'];
  values: ScheduleValues;
};

export const DirectEngagementsFormScheduleCard = ({
  account,
  companyLocationInputValue,
  setCompanyLocationInputValue,
  setShowWorksiteModal,
  setValues,
  values,
}: DirectEngagementsFormScheduleCardProps) => {
  return (
    <Card>
      <CardHeader>
        <SmallHeader>
          When and where do you need the work performed?
        </SmallHeader>
      </CardHeader>
      <CardSection>
        <Grid>
          <GridRow gutterBottom="24px">
            <GridColumn
              largeScreenWidth={4}
              smallScreenWidth={12}
              tabletWidth={5}
            >
              <Dropdown
                name="billingUnitsPerWeek"
                label="Estimated hours per week (5 - 40)"
                options={billingUnitValues.map(value => ({
                  label: value,
                  value,
                }))}
                displayKey="label"
                valueKey="label"
                disableArrowKeyChange
              />
            </GridColumn>
            <GridColumn
              largeScreenWidth={4}
              smallScreenWidth={12}
              tabletWidth={3}
            >
              <Gutter top="24px" only="mobile" />
              <DateInput
                name="startDate"
                minDate={new Date()}
                label="Estimated start date"
                direction={rawDateInputDirections.Down}
                onChange={async value => {
                  await setValues({
                    ...values,
                    endDate:
                      EngagementUtil.calculateEndDateFromEstimatedDuration(
                        value?.toString() ?? '',
                        values.estimatedDuration as EstimatedDuration
                      ),
                  });
                }}
              />
            </GridColumn>
            <GridColumn
              largeScreenWidth={4}
              smallScreenWidth={12}
              tabletWidth={3}
            >
              <Gutter top="24px" only="mobile" />
              <Dropdown
                name="estimatedDuration"
                label="Estimated duration"
                options={Object.keys(estimatedDurationValues).map(
                  (key, index) => ({
                    label: Object.values(estimatedDurationValues)[index],
                    value: key,
                  })
                )}
                onChange={async value => {
                  await setValues({
                    ...values,
                    endDate:
                      EngagementUtil.calculateEndDateFromEstimatedDuration(
                        values.startDate ?? '',
                        value as EstimatedDuration
                      ),
                  });
                }}
                displayKey="label"
                valueKey="value"
                disableArrowKeyChange
              />
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn width={12}>
              <Well background="light blue">
                <Grid>
                  <GridColumn
                    smallScreenWidth={12}
                    tabletWidth={6}
                    largeScreenWidth={6}
                  >
                    <Dropdown
                      name="worksite"
                      label="Do you need the work done in your office?"
                      options={Object.keys(worksiteValues).map(
                        (key, index) => ({
                          label: Object.values(worksiteValues)[index],
                          value: key,
                        })
                      )}
                      displayKey="label"
                      valueKey="value"
                      onChange={async value => {
                        switch (value) {
                          case PositionsConst.Worksites.OnsiteRemote:
                          case PositionsConst.Worksites.Onsite: {
                            if (
                              values.companyLocation?.locationName ===
                              ProjectNeedsDefaultRemoteLocation.locationName
                            ) {
                              await setValues({
                                ...values,
                                companyLocation: companyLocationInputValue,
                                addressCountryCode:
                                  DirectEngagementsFormIntakeUtil.defaultCountryCode(
                                    { account }
                                  ),
                              });
                            } else {
                              setCompanyLocationInputValue(
                                values.companyLocation
                              );
                            }
                            // only pop modal when switching from Remote
                            // or on first selection
                            if (
                              values.worksite === 'Remote' ||
                              !values.worksite
                            ) {
                              setShowWorksiteModal(true);
                            }
                            break;
                          }
                          default: {
                            await setValues({
                              ...values,
                              companyLocation:
                                ProjectNeedsDefaultRemoteLocation,
                            });
                          }
                        }
                      }}
                      disableArrowKeyChange
                    />
                  </GridColumn>

                  <GridColumn
                    smallScreenWidth={12}
                    tabletWidth={6}
                    largeScreenWidth={6}
                  >
                    {values.worksite === PositionsConst.Worksites.Remote && (
                      <Dropdown
                        name="addressCountryCode"
                        label="Location"
                        options={Object.entries(selfServiceCountryCodes).map(
                          ([key, value]) => ({
                            label: value,
                            value: key,
                          })
                        )}
                        displayKey="label"
                        valueKey="value"
                        disableArrowKeyChange
                      />
                    )}
                    {values.worksite !== PositionsConst.Worksites.Remote &&
                      !!values.worksite && (
                        <LayoutItem>
                          <Gutter top="24px" only="mobile" />
                          <Location
                            name="companyLocation"
                            label="Office street address"
                            onChange={value => {
                              if (value) {
                                setCompanyLocationInputValue(value);
                              }
                            }}
                            disableArrowKeyChange
                          />
                        </LayoutItem>
                      )}
                  </GridColumn>
                </Grid>
              </Well>
            </GridColumn>
          </GridRow>
        </Grid>
      </CardSection>
    </Card>
  );
};
